import { Component, ViewChild } from '@angular/core';
import { ButtonComponent } from 'components/input/button/button.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { CardComponent } from 'components/layout/card/card.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { ProductVariantModel } from 'models/generated/Entities.GormCrm.ProductVariant';
import { CompanySearchResultViewModel } from 'models/generated/Models.CompanySearchResultView';
import { CompanyDtoModel, ContactDtoModel, NewOrderRequestModel, ProductDtoModel, ProductPlanDiscontinuationDtoModel, SellerDtoModel } from 'models/generated/Models.NewOrderRequest';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CompanyService } from 'services/company.service';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductService } from 'services/product.service';
import { ProductPlanService } from 'services/productplan.service';
import { SalesService } from 'services/sales.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'debug-order',
	templateUrl: './debug-order.component.html',
	providers: [FormValidationService],
	imports: [Layout, CustomSelectComponent, CustomInputComponent, CardComponent, ListView, CurrencyPipe, TranslatePipe]
})
@ComponentUrl('debug-order')
@CredentialRequired(CredentialType.AccessDebugViews)
export class DebugOrderComponent {

	@ViewChild("companysearch") companySearch!: CustomSelectComponent;
	@ViewChild("submitorder") submitOrderButton!: ButtonComponent;

	inputTypeEnum = InputType;

	newOrder?: NewOrderRequestModel;

	public pendingSearch?: Subscription;
	private currentSearch: { base: string, results: CompanySearchResultViewModel[] } = {
		base: '',
		results: []
	};
	private matchingResults: CompanySearchResultViewModel[] = [];

	companyResults: SelectListItemModel[] = [];
	companyProductPlans: SelectListItemModel[] = [];
	productVariantItems: SelectListItemModel[] = [];
	paymentIntervalItems: SelectListItemModel[] = [new SelectListItemModel(1, "1"), new SelectListItemModel(3, "3"), new SelectListItemModel(12, "12")];
	startupRateItems: SelectListItemModel[];

	productVariants: ProductVariantModel[] = [];

	newOrderCompanyId?: string;
	newOrderLineVariantId?: string;
	newOrderLinePrice?: number;
	newOrderLineInstallments?: number;
	newOrderLineStartupAmount?: number;
	newOrderLineStartupInstallments = 1;
	newOrderLineInterval = 12;
	newOrderLineBillingPlanBasedOnProductPlanId?: string;
	discontinueProductPlanId?: string;
	discontinueAdjustmentAmount?: number;
	discontinueRemainingAmount?: number;

	constructor(private readonly salesService: SalesService, private readonly companyService: CompanyService, private readonly userService: UserService, private readonly productService: ProductService, private readonly productPlanService: ProductPlanService, private readonly notificationService: NotificationService) {
		let startupInstallments = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
		this.startupRateItems = startupInstallments.map(rate => new SelectListItemModel(rate, rate.toString()));
		this.productService.getAllActiveProducts().then(products => {
			this.productVariants = products
				.flatMap(pv => pv.productVariants);
			this.productVariants.sort((a, b) => this.productService.getVariantName(a).localeCompare(this.productService.getVariantName(b))).forEach(pv => {
				this.productVariantItems.push(new SelectListItemModel(pv.id!, this.productService.getVariantName(pv)));
			});
		});
	}

	async changeCompany(companyId: string) {
		this.newOrder = undefined;
		await this.productPlanService.getProductPlansByCompanyId(companyId).then(plans => {
			this.companyProductPlans = plans.map(plan => new SelectListItemModel(plan.id!, plan.productName));
		});
		this.newOrder = new NewOrderRequestModel();
	}

	async createOrder() {
		if (!this.newOrder || !this.newOrderCompanyId) return;

		var company = await this.companyService.getCompany(this.newOrderCompanyId);
		let orderCompany = new CompanyDtoModel();
		orderCompany.vatNumber = company.vatNumber;
		orderCompany.name = company.name;
		orderCompany.address = company.address;
		orderCompany.city = company.city;
		orderCompany.countryCode = 'dk';
		orderCompany.zipcode = company.zipcode;
		this.newOrder.company = orderCompany;

		let orderContact = new ContactDtoModel();
		orderContact.firstName = company.primaryContact?.firstName ?? '';
		orderContact.lastName = company.primaryContact?.lastName ?? '';
		orderContact.email = company.primaryContact?.emailAddress ?? '';
		orderContact.mobilePhone = company.primaryContact?.contactPhones[0]?.number ?? '';
		orderContact.phone = company.primaryContact?.contactPhones[0]?.number ?? '';
		this.newOrder.contact = orderContact;

		this.newOrder.seller = new SellerDtoModel();
		this.newOrder.seller.gormUserId = this.userService.getCurrentUser().id;

		this.newOrder.signDate = new Date();

		await this.salesService.createOrder(this.newOrder);
		this.notificationService.success('Order created');
		this.submitOrderButton.succeeded();
		this.newOrder = new NewOrderRequestModel();
	}

	updatePrice() {
		if (!this.newOrderLineVariantId) return;
		this.newOrderLinePrice = this.productVariants.find(pv => pv.id === this.newOrderLineVariantId)?.price;
		this.newOrderLineStartupAmount = this.newOrderLinePrice ?? 0 > 0  ? this.newOrderLinePrice! * 20 / 100 : 0;
	}

	getProductName(variantId: string) {
		return this.productService.getVariantName(this.productVariants.find(pv => pv.id === variantId)!);
	}

	getDiscontinuedProductName(productPlanId: string) {
		return this.companyProductPlans.find(p => p.value === productPlanId)?.name;
	}

	addProduct() {
		if (!this.newOrderLineVariantId) return;
		let newProduct = new ProductDtoModel();
		newProduct.productVariantId = this.newOrderLineVariantId;
		newProduct.quantity = 1;
		newProduct.unitPrice = this.newOrderLinePrice ?? 0;
		if (this.newOrderLineInstallments && this.newOrderLineInstallments > 0) newProduct.numberOfInstallments = this.newOrderLineInstallments;
		newProduct.paymentInterval = this.newOrderLineInterval;
		newProduct.startupAmount = this.newOrderLineStartupAmount;
		newProduct.startupInstallments = this.newOrderLineStartupInstallments;
		newProduct.generateInvoicesBasedOnProductPlanId = this.newOrderLineBillingPlanBasedOnProductPlanId;
		this.newOrder!.products.push(newProduct);
	}

	addDiscontinuedProduct() {
		let discontinuedProduct = new ProductPlanDiscontinuationDtoModel();
		discontinuedProduct.productPlanId = this.discontinueProductPlanId;
		discontinuedProduct.adjustmentAmount = this.discontinueAdjustmentAmount!;
		discontinuedProduct.remainingAmount = this.discontinueRemainingAmount!;

		this.newOrder!.productPlanDiscontinuations.push(discontinuedProduct);
	}

	removeProduct(product: ProductDtoModel) {
		this.newOrder!.products = this.newOrder!.products.filter(p => p !== product);
	}

	removeDiscontinuedProduct(product: ProductPlanDiscontinuationDtoModel) {
		this.newOrder!.productPlanDiscontinuations = this.newOrder!.productPlanDiscontinuations.filter(p => p !== product);
	}

	searchCompanies() {
		let query = this.companySearch.displayValue.trim();

		if (query.length < 2) {
			this.companyResults = [];
			return;
		}

		if (this.currentSearch.base !== '' && query.substring(0, this.currentSearch.base.length) === this.currentSearch.base) {
			if (!this.pendingSearch) this.filterCompanyResults();
			return;
		}
		if (this.pendingSearch) this.pendingSearch.unsubscribe();
		this.currentSearch.base = query.substring(0, 4);
		this.companyResults = [];
		this.pendingSearch = this.companyService.searchCompaniesByKeyword(this.currentSearch.base).subscribe((results: CompanySearchResultViewModel[]) => {
			this.currentSearch.results = results;
			this.filterCompanyResults();
			this.pendingSearch = undefined;
		});
	}

	private filterCompanyResults() {
		this.matchingResults = this.currentSearch.results.filter(result => {
			return result.companyId && this.nameMatch(result);
		}).sort((a, b) => a.companyName.localeCompare(b.companyName));
		let value = this.companySearch.displayValue;
		this.companyResults = this.matchingResults.map(result => new SelectListItemModel(result.companyId!, result.companyName));
		setTimeout(() => {
			this.companySearch.displayValue = value;
			this.companySearch.setFocus();
		});
	}

	private nameMatch(result: CompanySearchResultViewModel) {
		return result.companyName.toLowerCase().indexOf(this.companySearch.displayValue.trim().toLowerCase()) >= 0;
	}
}
